import { Controller } from 'stimulus';
import {
  STATUS_OPTIONS,
  AVAILABLE_STATUS_OPTIONS,
  AVAILABLE_STATUS_FORM_FIELDS,
  PAID_STATUS_DISABLED_OPTIONS,
} from '../src/js/opportunities/status_options';

class OpportunityStatusController extends Controller {
  initialize() {
    this.type = null;
    this.status = null;
    this.invoicePaid = false;
  }

  static targets = [
    'closingPrice',
    'contactContainer',
    'editAddress',
    'formField',
    'modal',
    'referralLabel',
    'statusSelect',
    'streetAddress',
    'submitButton',
    'updateStatusForm',
  ];

  /**
   * Click handler for triggering the Opportunity Update Status form. Grabs
   * value of `[data-status]` and `[data-type]` and sets them as
   * `this.status` and `this.type` respectively.
   * invokes `this._toggleFormFieldDisplay`.
   * When this is fired the form/modal is opened.
   * Invokes `validateAddress`.
   * If an element with the data-target of `editAddress` does not have the class
   * `u-displayNone`, toggle the elements in contactContainer to hide the edit
   * address form by default when the modal is opened.
   *
   * @param {Event} e Click event on Update Status `button`
   * @constant {Controller} toggleController grabs reference to the
   * toggleController with the context of `contactContainer` data-target
   * @returns {void}
   */
  updateFormFields(e) {
    const toggleController =
      this.application.getControllerForElementAndIdentifier(
        this.contactContainerTarget,
        'toggle'
      );

    this.status = e.target.dataset.status;
    this.type = e.target.dataset.type;
    this.invoicePaid = e.target.dataset.paid;

    this._toggleFormFieldDisplay();
    this._updateSelectOptions();
    this._validateAddress();

    if (!this.editAddressTarget?.classList.contains('u-displayNone')) {
      toggleController?.toggle();
    }
  }

  /**
   * Change handler for updating opportunity Status in the form's `select`.
   * Grabs value of the newly selected status and sets it as `this.status`.
   * Invokes `this._toggleFormFieldDisplay`.
   *
   * @param {Event} e change event on Update Status `select`
   * @returns {void}
   */
  handleChangeStatus(e) {
    this.status = e.target.value;

    this._toggleFormFieldDisplay();

    if (this.status === 'pending' || this.status === 'closed') {
      this._validateAddress();
    }
  }

  /**
   * *Currently this is only hooked up for validating opportunity address input,
   *   but written in a way to support any future additional validation checks*
   * If status is pending or closed, invokes `this._validateAddress()`.
   * If status is closed and input is not valid, prevents event from bubbling to
   * prevent form submission.
   * @param {Event} e Click event on modal footer button, triggers form submission
   * @returns {Boolean} whether or not the opportunity address input is valid
   */
  validate(e) {
    let valid = true;
    if (this.status === 'pending' || this.status === 'closed') {
      valid = this._validateAddress();
    } else {
      return valid;
    }

    if (!valid) {
      e.stopPropagation();
      e.preventDefault();
    }

    return valid;
  }

  /**
   * Given the opportunity status, replace existing options in the form's
   * status `select` element based on what options should be available for that
   * status as listed in `AVAILABLE_STATUS_OPTIONS`. Iterates over the array of
   * available status options, and for each option creates a new `option` with
   * the appropriate value/text and adds it to the
   * `select` (`this.statusSelectTarget`).
   *
   * @returns {void}
   */
  _updateSelectOptions() {
    // clear out all options before adding in new options (to avoid duplicates)
    this.statusSelectTarget.options.length = 0;

    const availableStatusOptions =
      AVAILABLE_STATUS_OPTIONS[this.type][this.status];

    availableStatusOptions.forEach((statusOption) => {
      if (statusOption === 'listing' || statusOption === 'buyer') {
        if (this.invoicePaid === 'true') {
          // if invoice is paid, prevent status moving back to 'listing' or 'buyer'
          return;
        }
      }

      const option = document.createElement('option');

      option.value = statusOption;
      option.text = STATUS_OPTIONS[statusOption];
      option.setAttribute('data-testid', statusOption);
      option.selected = statusOption === this.status;

      this.statusSelectTarget.add(option);
    });
  }

  /**
   * Given the opportunity status, show/hide appropriate form fields based on
   * what fields should be available for that status as listed in
   * `AVAILABLE_STATUS_FORM_FIELDS`. Iterates over all form fields
   * (`this.formFieldTargets`) and for each field sets the display to either
   * `''` or `'none'` and will disable all fields that are not shown while
   * enabling all fields that are shown.
   * If the invoice is paid, pass each field to `_handlePaidStatus`.
   * If the status is listing, change the closing price label to "Listing Price"
   * and add `u-displayNone` to the referralLabelTarget.
   * If the status is "pending", change the closing price label to "Projected Closing Price"
   * and remove `u-displayNone` from the referralLabelTarget.
   * If the status is "closed", change the closing price label to "Closing Price"
   * and remove `u-displayNone` from the referralLabelTarget.
   *
   * @returns {void}
   */
  _toggleFormFieldDisplay() {
    this.formFieldTargets.forEach((field) => {
      const fieldName = field.dataset.name;

      if (
        AVAILABLE_STATUS_FORM_FIELDS[this.type][this.status].includes(fieldName)
      ) {
        field.style.display = '';

        if ('alwaysDisabled' in field.dataset) {
          return;
        }

        this._enableInput(field);
      } else {
        field.style.display = 'none';
        this._disableInput(field);
      }

      if (PAID_STATUS_DISABLED_OPTIONS.includes(fieldName)) {
        this._handlePaidStatus(field);
      }
    });

    if (this._labelActions.hasOwnProperty(this.status)) {
      this._labelActions[this.status]();
    }
  }

  /* eslint-disable no-invalid-this */
  _labelActions = {
    listing: () => {
      this.closingPriceTarget.textContent = 'Listing Price';
      this.referralLabelTarget.classList.add('u-displayNone');
    },
    pending: () => {
      this.closingPriceTarget.textContent = 'Projected Closing Price';
      this.referralLabelTarget.classList.remove('u-displayNone');
    },
    closed: () => {
      this.closingPriceTarget.textContent = 'Closing Price';
      this.referralLabelTarget.classList.remove('u-displayNone');
    },
  };
  /* eslint-enable no-invalid-this */

  /**
   * Given a form field, determine whether or not this field should be disabled
   * based on the list of field names found in PAID_STATUS_DISABLED_OPTIONS.
   * If the field should be disabled, pass the field to `_disableInput`.
   *
   * @param {HTMLElement} field Parent element of input to be disabled
   * @param {String} fieldName name of the field to disable
   * @returns {void}
   */
  _handlePaidStatus(field) {
    if (this.invoicePaid === 'true') {
      this._disableInput(field);
    }
  }

  /**
   * Validates address input via invoking `validateField` given the input's respective
   * `ValidationController` instance. If invalid, toggle the input given the input's
   * respective `ToggleController`.
   * @returns {Boolean} whether or not address input is valid
   */
  _validateAddress() {
    // validate address input
    // if invalid, toggle open, display invalid message
    const validationController =
      this.application.getControllerForElementAndIdentifier(
        this.updateStatusFormTarget,
        'validation'
      );
    const toggleController =
      this.application.getControllerForElementAndIdentifier(
        this.contactContainerTarget,
        'toggle'
      );

    validationController?.removeExistingErrorMessage(this.streetAddressTarget);

    let isValid = true;

    if (this.status === 'pending' || this.status === 'closed') {
      isValid = validationController.validateField(this.streetAddressTarget);

      if (!isValid) {
        toggleController.toggle();
      }
    }

    return isValid;
  }

  /**
   * Given an element, drill down through the descendants, find the `input`,
   * and disable it.
   *
   * @param {HTMLElement} parentElem Input element to disable
   * @returns {void}
   */
  _disableInput(parentElem) {
    parentElem?.querySelector('input')?.setAttribute('disabled', 'true');
  }

  /**
   * Given an element, drill down through the descendants, find the `input`,
   * and enable it.
   *
   * @param {HTMLElement} parentElem Input element to disable
   * @returns {void}
   */
  _enableInput(parentElem) {
    parentElem?.querySelector('input')?.removeAttribute('disabled');
  }
}

export default OpportunityStatusController;
